import React from 'react';
import { CarpoolVehicle } from '../types';
import { formatDate, isExpiringSoon, isExpired, getDaysUntilExpiry } from '../utils/dateUtils';
import { Car, Calendar, Euro, AlertTriangle, Move } from 'lucide-react';

interface VehicleCardProps {
  vehicle: CarpoolVehicle;
  onClick?: (vehicle: CarpoolVehicle) => void;
  onSetActive?: (id: number) => void;
  onStartDrag?: () => void;
  dragMode?: boolean;
  onStopDrag?: () => void;
}

// Hilfsfunktion für Anzeige
const getValue = (field: any, fallback = '') => typeof field === 'object' && field !== null ? field.value : field || fallback;

const VehicleCard: React.FC<VehicleCardProps> = ({ vehicle, onClick, onStartDrag, dragMode, onSetActive }) => {
  const formatCurrency = (value: string | undefined): string => {
    if (!value) return '0,00 €';
    const num = parseFloat(value);
    return isNaN(num) ? '0,00 €' : `${num.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
  };

  const getContractStatus = () => {
    if (!vehicle.end_of_contract) return null;
    const daysUntilExpiry = getDaysUntilExpiry(vehicle.end_of_contract);
    if (isExpired(vehicle.end_of_contract)) {
      return { text: 'Vertrag abgelaufen', color: 'bg-[#ef4444] text-[#ffffff] border border-[#ef4444] bg-opacity-90' };
    }
    if (isExpiringSoon(vehicle.end_of_contract, 90)) {
      return { text: `${daysUntilExpiry} Tage bis Ablauf`, color: 'bg-[#facc15] text-[#111111] border border-[#facc15] bg-opacity-90' };
    }
    return { text: 'Vertrag aktiv', color: 'bg-[#add015] text-[#111111] border border-[#add015] bg-opacity-90' };
  };

  const getTuvStatus = () => {
    if (!vehicle.tuev_until) return null;
    const daysUntilExpiry = getDaysUntilExpiry(vehicle.tuev_until);
    if (isExpired(vehicle.tuev_until)) {
      return { text: 'TÜV abgelaufen', color: 'bg-[#ef4444] text-[#ffffff] border border-[#ef4444] bg-opacity-90' };
    }
    if (isExpiringSoon(vehicle.tuev_until, 30)) {
      return { text: `${daysUntilExpiry} Tage bis TÜV`, color: 'bg-[#facc15] text-[#111111] border border-[#facc15] bg-opacity-90' };
    }
    return { text: 'TÜV gültig', color: 'bg-[#add015] text-[#111111] border border-[#add015] bg-opacity-90' };
  };

  const contractStatus = getContractStatus();
  const tuvStatus = getTuvStatus();

  return (
    <div
      className={`rounded-xl shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer border relative z-0 h-[600px] flex flex-col ${dragMode ? 'animate-wiggle' : ''} bg-[#ffffff] dark:bg-[#181c20] border-gray-200 dark:border-[#23272e] overflow-hidden`}
      style={{ color: '#111111' }}
      onClick={() => onClick?.(vehicle)}
      draggable={dragMode}
      onDragStart={onStartDrag}
    >
      {/* Drag Handle - nur sichtbar im Drag-Modus */}
      {dragMode && (
        <div className="absolute top-3 right-3 z-10">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full">
            <Move className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </div>
        </div>
      )}
      
      <div className="p-6 flex flex-col h-full text-[#111111] dark:text-[#f1f5f9]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#add015] rounded-lg">
              <Car className="w-6 h-6" style={{ color: '#111111', filter: 'drop-shadow(0 1px 1px #23272e33)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#111111] dark:text-[#f1f5f9]">{vehicle.model}</h3>
              <p className="text-sm text-[#111111] dark:text-[#f1f5f9] opacity-70">{getValue(vehicle.manufacturer, '')}</p>
              {vehicle.vin && vehicle.vin !== '' && (
                <p className="text-xs text-[#111111] dark:text-[#f1f5f9] opacity-50">FIN: {vehicle.vin}</p>
              )}
            </div>
          </div>
          {vehicle.poolcar && (
            <span className="px-2 py-1 bg-[#add015] text-[#111111] text-xs font-medium rounded-full border border-[#add015] dark:shadow dark:shadow-black/10">
              Poolcar
            </span>
          )}
        </div>

        {/* License Plate and Versteuerung */}
        <div className="flex items-center justify-between mb-4">
          <div className="bg-[#23272e] text-white px-3 py-2 rounded-lg font-bold text-center">
            {vehicle.license_plate}
          </div>
          {getValue(vehicle.taxation) && getValue(vehicle.taxation) !== 'Keine Versteuerung' && (
            <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
              {getValue(vehicle.taxation)}
            </span>
          )}
        </div>

        {/* Vehicle Type */}
        <div className="mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-[#111111] dark:text-[#f1f5f9]">FAHRZEUGTYP</span>
          </div>
          <p className="text-sm text-[#111111] dark:text-[#f1f5f9] opacity-70">{getValue(vehicle.type)}</p>
        </div>

        {/* Kilometerstand */}
        <div className="mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-[#111111] dark:text-[#f1f5f9]">KILOMETERSTAND</span>
          </div>
          <p className="text-lg font-semibold text-[#111111] dark:text-[#f1f5f9]">
            {(() => {
              const km = vehicle.km;
              // Wenn km null, undefined, leer oder 0 ist, zeige 0
              if (!km || km === 0) {
                return '0 km';
              }
              // Konvertiere zu Number und formatiere
              const kmNumber = typeof km === 'string' ? parseInt(km, 10) : km;
              return `${kmNumber.toLocaleString('de-DE')} km`;
            })()}
          </p>
        </div>

        {/* Status Cards */}
        <div className="space-y-3 mb-4">
          {/* Contract Type */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-[#111111] dark:text-[#f1f5f9]">Vertrag</span>
            </div>
            <span className="text-sm text-[#111111] dark:text-[#f1f5f9]">
              {getValue(vehicle.type_of_contract) || 'Nicht angegeben'}
            </span>
          </div>

          {/* Contract Start - immer bei Leasing/Finanzierung anzeigen */}
          {(() => {
            const contractType = getValue(vehicle.type_of_contract, '').toLowerCase();
            const showDates = contractType.includes('leasing') || contractType.includes('finanzierung');
            
            if (!showDates) return null;
            
            return (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#111111] dark:text-[#f1f5f9]">Vertragsbeginn</span>
                <span className="text-sm text-[#111111] dark:text-[#f1f5f9]">
                  {vehicle.start_of_contract ? formatDate(vehicle.start_of_contract) : '-'}
                </span>
              </div>
            );
          })()}

          {/* Contract End - immer bei Leasing/Finanzierung anzeigen */}
          {(() => {
            const contractType = getValue(vehicle.type_of_contract, '').toLowerCase();
            const showDates = contractType.includes('leasing') || contractType.includes('finanzierung');
            
            if (!showDates) return null;
            
            return (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#111111] dark:text-[#f1f5f9]">Vertragsende</span>
                <div className="text-right">
                  <p className="text-sm text-[#111111] dark:text-[#f1f5f9]">
                    {vehicle.end_of_contract ? formatDate(vehicle.end_of_contract) : '-'}
                  </p>
                  {contractStatus && vehicle.end_of_contract && (
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${contractStatus.color}`}>
                      {contractStatus.text}
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* TÜV Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-[#111111] dark:text-[#f1f5f9]">TÜV</span>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#111111] dark:text-[#f1f5f9]">
                {vehicle.tuev_until ? formatDate(vehicle.tuev_until) : 'Nicht angegeben'}
              </p>
              {tuvStatus && (
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${tuvStatus.color}`}>
                  {tuvStatus.text}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Monthly Costs */}
        <div className="mt-auto">
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="flex items-center space-x-2">
              <Euro className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-[#111111] dark:text-[#f1f5f9]">Monatliche Kosten</span>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#111111] dark:text-[#f1f5f9]">Netto: {formatCurrency(vehicle.monthly_net_sum)}</p>
              <p className="text-sm text-[#111111] dark:text-[#f1f5f9] opacity-70">Brutto: {formatCurrency(vehicle.monthly_gross_sum)}</p>
            </div>
          </div>

          {/* Letzter KM-Check */}
          <div className="flex items-center justify-between pt-2">
            <span className="text-xs text-[#111111] dark:text-[#f1f5f9] opacity-50">Letzter KM-Check</span>
            <span className="text-xs text-[#111111] dark:text-[#f1f5f9] opacity-50">
              {vehicle.date_of_last_km_check ? formatDate(vehicle.date_of_last_km_check) : 'Nie'}
            </span>
          </div>

          {/* Fahrzeugbilder bewusst im Dashboard ausgeblendet */}
        </div>
      </div>
    </div>
  );
};

export default VehicleCard; 